/**
 * Module dependencies.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useBankAccountFirstRefreshTransactions } from 'app/hooks/requests/bank-accounts/use-bank-account-fisrt-refresh-transactions';
import { useCallback } from 'react';
import { appRoutes } from 'app/routes';
import { Loading } from 'app/components/atoms/loading/loading';
import React from 'react';
import { useBankAccountLinkStatus } from 'app/hooks/requests/bank-accounts/use-bank-account-status';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { DatePickerField } from 'app/components/atoms/forms/fields/date-picker/date-picker-field';
import { formatCurrency } from 'app/core/utils/formatter';
import moment from 'moment';

/**
 * `Stat` — a compact label-over-value cell for the details grid.
 */

function Stat({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className={'min-w-0'}>
      <div className={'text-[11px] font-medium uppercase tracking-wide text-muted-foreground'}>{label}</div>

      <div className={'truncate text-sm font-medium text-foreground'}>{value}</div>
    </div>
  );
}

/**
 * `Props` type.
 */

type Props = {
  accountId: string;
  integrationId: string;
  onDone: () => void;
};

/**
 * Export `BankAccountSyncForm` component.
 *
 * The sync step (pick how far back to import + start). Driven by props so it can
 * render inside a modal (from the connect page) or as the standalone route (the
 * Open-Banking redirect return).
 */

export function BankAccountSyncForm({ accountId, integrationId, onDone }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { mutateAsync: refreshTransactions, isLoading: isLoadingRefreshTransactions } =
    useBankAccountFirstRefreshTransactions();

  const { data, isLoading } = useBankAccountLinkStatus(integrationId);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({
    mode: 'all'
  });

  const onSubmit = useCallback(
    async ({ date }: any) => {
      try {
        await refreshTransactions({
          accountId: parseFloat(accountId ?? '-1'),
          integrationId: integrationId ?? '',
          transactionsDate: date ? date.toISOString() : undefined
        });

        onDone();
      } catch (error: any) {
        console.log(error);
      }
    },
    [accountId, integrationId, onDone, refreshTransactions]
  );

  return (
    <Loading isLoading={isLoading}>
      {!!data && (
        <div className={'space-y-4'}>
          {/* Identity header: logo + name/iban + balance */}
          <div className={'flex items-center gap-3 rounded-lg border border-border p-3'}>
            {data.image ? (
              <img
                alt={data.name}
                className={'h-12 w-12 shrink-0 rounded-lg border border-border bg-white object-contain p-1'}
                src={data.image}
              />
            ) : (
              <div
                className={
                  'grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-primary/10 text-sm font-semibold text-primary'
                }
              >
                {(data.name ?? '?').slice(0, 2).toUpperCase()}
              </div>
            )}

            <div className={'min-w-0'}>
              <div className={'truncate font-semibold'}>{data.name}</div>

              {!!data.iban && <div className={'truncate font-mono text-xs text-muted-foreground'}>{data.iban}</div>}
            </div>

            <div className={'ml-auto shrink-0 text-right'}>
              <div className={'text-[11px] font-medium uppercase tracking-wide text-muted-foreground'}>
                {translate('common.labels.balance')}
              </div>

              <div className={"font-['Oswald'] font-semibold"}>
                {formatCurrency(data.balance, { locale: 'en', currency: data.currency })}
              </div>
            </div>
          </div>

          {/* Details grid */}
          <div className={'grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg bg-muted/40 p-4 sm:grid-cols-3'}>
            {!!data.bic && <Stat label={translate('common.labels.bic')} value={data.bic} />}

            {!!data.country && <Stat label={translate('common.labels.country')} value={data.country} />}

            {!!data.currency && <Stat label={translate('common.labels.currency')} value={data.currency} />}
          </div>
        </div>
      )}

      <form className={'mt-5'} onSubmit={handleSubmit(onSubmit)}>
        {!data?.reconnectState && (
          <>
            <p className={'mb-2 text-sm text-muted-foreground'}>{translate('bankAccounts.sync.startSyncing')}</p>

            <DatePickerField
              control={control}
              disabledDate={(date: any) => {
                const days = date.diff(moment(), 'days');
                const max = parseInt('90', 10) * -1;

                return days >= 0 || days <= max;
              }}
              label={translate('common.labels.date')}
              name={'date'}
              placeholder={translate('common.labels.date')}
              rules={{}}
            />
          </>
        )}

        <div className={'mt-4 flex justify-end'}>
          <SubmitButton
            className={'mt-0'}
            disabled={isSubmitting}
            label={translate(data?.reconnectState ? 'common.actions.finish' : 'common.actions.startSync')}
            loading={isLoadingRefreshTransactions}
          />
        </div>
      </form>
    </Loading>
  );
}

/**
 * Export `BankAccountSync` component.
 *
 * Standalone route used when returning from the bank's Open-Banking redirect.
 */

export function BankAccountSync(): JSX.Element {
  const params = useParams();
  const navigate = useNavigate();

  return (
    <div className={'mx-auto w-full max-w-[500px] pt-10'}>
      <BankAccountSyncForm
        accountId={params.accountId ?? ''}
        integrationId={params.integrationId ?? ''}
        onDone={() => navigate(appRoutes.dashboard.bankAccounts.accounts.base)}
      />
    </div>
  );
}
